import { byId, $, $$ } from "@utils/dom";
import { openDialog } from "@components/dialog";
import { clearElements } from "@services/storage/elements";
import { clearFilenames } from "@services/storage/filenames";
import { clearSkinini } from "@services/storage/skinini";
import { downloadSkinFromData, formatBytes } from "@services/download/download";
import {
  incrementSkinDownloadCount,
  toggleSkinFavorite,
} from "@services/api/skin";
import { STORAGE_KEYS } from "@constants/storage-keys";
import { CSS } from "@constants/css-classes";
import { DATA_ATTR } from "@constants/data-attributes";
import type { StoredElement } from "@app-types/element.types";
import type { FilePathTuple } from "@app-types/storage.types";

interface RemixData {
  skin_id: string | null;
  elements: Record<string, StoredElement>;
  filenames: Record<string, FilePathTuple[]>;
  skinini: Record<string, string | number>;
}

/**
 * Get remix data from the embedded JSON script tag
 */
function getRemixData(): RemixData | null {
  const script = byId("remixData");
  if (!script) return null;

  try {
    return JSON.parse(script.textContent || "");
  } catch {
    console.error("Failed to parse remix data");
    return null;
  }
}

/**
 * Handle Remix button click
 * Shows confirmation dialog, then replaces localStorage with skin data
 */
async function handleRemix(): Promise<void> {
  const result = await openDialog("remixDialog");

  if (result === "confirm") {
    const remixData = getRemixData();
    if (!remixData) {
      console.error("No remix data available");
      return;
    }

    // Clear all existing data
    clearElements();
    clearFilenames();
    clearSkinini();

    // Set new data directly to localStorage
    localStorage.setItem(
      STORAGE_KEYS.ELEMENTS,
      JSON.stringify(remixData.elements),
    );
    localStorage.setItem(
      STORAGE_KEYS.FILENAMES,
      JSON.stringify(remixData.filenames),
    );
    localStorage.setItem(
      STORAGE_KEYS.SKININI,
      JSON.stringify(remixData.skinini),
    );

    // Redirect to home page
    window.location.href = "/interface";
  }
}

/**
 * Handle Download button click
 * Downloads the skin using embedded remixData with progress feedback
 */
async function handleSkinDownload(event: Event): Promise<void> {
  const downloadButton = event.currentTarget as HTMLButtonElement;
  if (!downloadButton) return;

  const remixData = getRemixData();
  if (!remixData) {
    console.error("No remix data available");
    return;
  }

  // Get all placeholder elements (both desktop and mobile sidebars)
  const placeholders = $$(DATA_ATTR.SKIN_DOWNLOAD_PLACEHOLDER);
  const placeholderBacks = $$(DATA_ATTR.SKIN_DOWNLOAD_PLACEHOLDER_BACK);
  const statusSpans = $$(DATA_ATTR.SKIN_DOWNLOAD_STATUS);
  const downloadButtons = $$<HTMLButtonElement>(DATA_ATTR.SKIN_DOWNLOAD);

  const showPlaceholders = (): void => {
    placeholders.forEach((el) => {
      el.classList.remove(CSS.HIDDEN);
      el.classList.add(CSS.FLEX);
      const logo = $<HTMLElement>(".logo-downloading", el);
      logo?.classList.remove(CSS.LOGO_DOWNLOADING_STOP);
    });
    placeholderBacks.forEach((el) => {
      el.classList.remove(CSS.HIDDEN);
      el.classList.add(CSS.FLEX);
    });
  };

  const hidePlaceholders = (): void => {
    placeholders.forEach((el) => {
      el.classList.add(CSS.HIDDEN);
      el.classList.remove(CSS.FLEX);
    });
    placeholderBacks.forEach((el) => {
      el.classList.add(CSS.HIDDEN);
      el.classList.remove(CSS.FLEX);
    });
  };

  const updateProgress = (status: string): void => {
    statusSpans.forEach((el) => {
      el.textContent = status;
    });
  };

  const stopLogoAnimation = (): void => {
    placeholders.forEach((placeholder) => {
      const logoDownloading = $<HTMLElement>(".logo-downloading", placeholder);
      if (!logoDownloading) return;
      const svg = $<SVGElement>("svg", logoDownloading);
      if (svg) {
        svg.addEventListener(
          "animationiteration",
          () => {
            logoDownloading.classList.add(CSS.LOGO_DOWNLOADING_STOP);
          },
          { once: true },
        );
      } else {
        logoDownloading.classList.add(CSS.LOGO_DOWNLOADING_STOP);
      }
    });
  };

  const setButtonsDisabled = (disabled: boolean): void => {
    downloadButtons.forEach((btn) => {
      btn.disabled = disabled;
    });
  };

  try {
    setButtonsDisabled(true);
    showPlaceholders();
    updateProgress("preparing...");

    await downloadSkinFromData(
      remixData.filenames,
      remixData.skinini,
      (loaded, _total) => {
        updateProgress(formatBytes(loaded));
      },
      remixData.elements,
    );

    // Increment skin download count (fire and forget)
    if (remixData.skin_id) {
      incrementSkinDownloadCount(remixData.skin_id);
    }

    updateProgress("complete!");
    stopLogoAnimation();
    setTimeout(() => {
      hidePlaceholders();
      setButtonsDisabled(false);
    }, 2000);
  } catch (error) {
    console.error("Download failed:", error);
    updateProgress("download failed");
    stopLogoAnimation();
    setTimeout(() => {
      hidePlaceholders();
      setButtonsDisabled(false);
    }, 2000);
  }
}

/**
 * Handle Favorite button click
 * Shows login dialog if user is a guest, toggles favorite state if logged in
 */
async function handleSkinFavorite(event: MouseEvent): Promise<void> {
  // Show login dialog for guests
  const notLoggedInDialog = byId("notLoggedInDialog");
  if (notLoggedInDialog) {
    await openDialog("notLoggedInDialog");
    return;
  }

  const button = event.currentTarget as HTMLButtonElement;

  // Get skin_id from remixData
  const remixData = getRemixData();
  if (!remixData?.skin_id) {
    console.error("No skin ID available for favorite");
    return;
  }

  const skinId = remixData.skin_id;
  const wasFavorited = button.dataset.isFavorited === "true";

  // Get icon element for swapping
  const icon = button.querySelector("svg use");
  const iconOuter = button.querySelector("svg");
  const originalIcon =
    icon?.getAttribute("href") ||
    icon?.getAttributeNS("http://www.w3.org/1999/xlink", "href");

  // Disable button and show spinner
  button.disabled = true;
  button.classList.add("opacity-50");

  if (icon && iconOuter) {
    icon.setAttribute("href", "#iconSpinner");
    iconOuter.classList.add("spinner");
  }

  try {
    const result = await toggleSkinFavorite(skinId);

    // Update button state
    button.dataset.isFavorited = result.isFavorited ? "true" : "false";

    // Swap icon based on new state
    if (icon) {
      const newIcon = result.isFavorited ? "#iconStar" : "#iconStarOutline";
      icon.setAttribute("href", newIcon);
    }

    // Update button text
    const textNode = Array.from(button.childNodes).find(
      (node) => node.nodeType === Node.TEXT_NODE && node.textContent?.trim(),
    );
    if (textNode) {
      textNode.textContent = result.isFavorited ? "In Favorites" : "Favorite";
    }

    // Toggle button styling
    if (result.isFavorited) {
      button.classList.add(
        "bg-[#D59825]",
        "inset-shadow-none!",
        "text-black/80!",
        "hover:bg-[#D59825]/70!",
      );
    } else {
      button.classList.remove(
        "bg-[#D59825]",
        "inset-shadow-none!",
        "text-black/80!",
        "hover:bg-[#D59825]/70!",
      );
    }

    // Update favorite count if displayed
    const countElement = $<HTMLElement>("[data-skin-favorite-count]");
    if (countElement) {
      countElement.textContent = result.favoriteCount.toString();
    }
  } catch (error) {
    console.error("Skin favorite toggle failed:", error);

    // Restore original icon on error
    if (icon && originalIcon) {
      icon.setAttribute("href", originalIcon);
    }
    // Restore original state
    button.dataset.isFavorited = wasFavorited ? "true" : "false";
  } finally {
    // Restore button state
    button.disabled = false;
    button.classList.remove("opacity-50");
    if (iconOuter) {
      iconOuter.classList.remove("spinner");
    }
  }
}

/**
 * Initialize skin page options handlers
 */
export function initSkinOptions(): void {
  const remixButtons = $$(DATA_ATTR.SKIN_REMIX);
  const downloadButtons = $$(DATA_ATTR.SKIN_DOWNLOAD);
  const favoriteButtons = $$(DATA_ATTR.SKIN_FAVORITE);

  remixButtons.forEach((button) => {
    button.addEventListener("click", handleRemix);
  });

  downloadButtons.forEach((button) => {
    button.addEventListener("click", handleSkinDownload);
  });

  favoriteButtons.forEach((button) => {
    button.addEventListener("click", handleSkinFavorite);
  });
}
